// ======================================================================
// FILE:
// src/modules/finance/reports/balance_sheet/pages/BalanceSheetPage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Balance Sheet
//
// PURPOSE:
// Read-only Balance Sheet report with expandable hierarchy and
// drill-down to General Ledger / Journal.
//
// DRILL-DOWN:
//
// Balance Sheet
//     ↓
// Account Group
//     ↓
// Sub Group
//     ↓
// COA Account
//     ↓
// General Ledger Transactions
//     ↓
// Existing Journal View
//
// IMPORTANT:
// - No accounting calculations in React
// - Backend remains authoritative
// - Uses existing Balance Sheet API
// - Uses existing General Ledger API
// - Uses existing Journal View
// ======================================================================

import React, {
    useCallback,
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
} from "react-router-dom";

import AsyncSelect
    from "react-select/async";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import FinanceSummaryCards
    from "../../../components/FinanceSummaryCards";

import FinanceSearchPanel
    from "../../../components/FinanceSearchPanel";

import balanceSheetService
    from "../../../services/balanceSheetService";

import generalLedgerService
    from "../../../services/generalLedgerService";

import financeLookupService
    from "../../../services/financeLookupService";


// ======================================================================
// COMPONENT
// ======================================================================

export default function BalanceSheetPage() {

    const navigate =
        useNavigate();


    // ==================================================================
    // STATE
    // ==================================================================

    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        reportData,
        setReportData,
    ] = useState(null);


    const [
        errorMessage,
        setErrorMessage,
    ] = useState("");


    // ==================================================================
    // LOOKUP STATE
    // ==================================================================

    const [
        selectedCompany,
        setSelectedCompany,
    ] = useState(null);


    const [
        selectedFinancialYear,
        setSelectedFinancialYear,
    ] = useState(null);


    const [
        selectedFinancialPeriod,
        setSelectedFinancialPeriod,
    ] = useState(null);


    // ==================================================================
    // FILTERS
    // ==================================================================

    const [
        filters,
        setFilters,
    ] = useState({

        company_id:
            null,

        financial_year_id:
            null,

        financial_period_id:
            null,

        include_zero_balances:
            false,

    });


    // ==================================================================
    // EXPANDED GROUPS
    //
    // Stores group IDs.
    //
    // Example:
    //
    // {
    //     1: true,
    //     6: true,
    //     17: false
    // }
    // ==================================================================

    const [
        expandedGroups,
        setExpandedGroups,
    ] = useState({});


    // ==================================================================
    // EXPANDED ACCOUNTS
    //
    // Stores COA account IDs.
    // ==================================================================

    const [
        expandedAccounts,
        setExpandedAccounts,
    ] = useState({});


    // ==================================================================
    // ACCOUNT LEDGER DATA
    //
    // {
    //     accountId: {
    //         loading: true,
    //         rows: [],
    //         summary: {}
    //     }
    // }
    // ==================================================================

    const [
        accountLedgers,
        setAccountLedgers,
    ] = useState({});


    // ==================================================================
    // UPDATE FILTER
    // ==================================================================

    const updateFilter = useCallback(
        (
            field,
            value
        ) => {

            setFilters(
                previous => ({
                    ...previous,
                    [field]:
                        value,
                })
            );

        },
        []
    );


    // ==================================================================
    // COMPANY LOOKUP
    // ==================================================================

    const loadCompanies = async (
        inputValue = ""
    ) => {

        try {

            const response =
                await financeLookupService
                    .searchCompanies(
                        inputValue
                    );

            return (
                response || []
            ).map(
                company => ({

                    value:
                        company.id,

                    label:
                        company.company_name ||
                        company.name ||
                        `Company ${company.id}`,

                    data:
                        company,

                })
            );

        } catch (error) {

            console.error(
                "Failed to load companies:",
                error
            );

            return [];

        }

    };


    // ==================================================================
    // FINANCIAL YEAR LOOKUP
    // ==================================================================

    const loadFinancialYears = async (
        inputValue = ""
    ) => {

        try {

            const response =
                await financeLookupService
                    .searchFinancialYears(
                        inputValue
                    );

            return (
                response || []
            ).map(
                year => ({

                    value:
                        year.id,

                    label:
                        year.year_name ||
                        year.name ||
                        `Financial Year ${year.id}`,

                    data:
                        year,

                })
            );

        } catch (error) {

            console.error(
                "Failed to load financial years:",
                error
            );

            return [];

        }

    };


    // ==================================================================
    // FINANCIAL PERIOD LOOKUP
    // ==================================================================

    const loadFinancialPeriods = async (
        inputValue = ""
    ) => {

        if (
            !filters.financial_year_id
        ) {

            return [];

        }

        try {

            const response =
                await financeLookupService
                    .searchFinancialPeriods(
                        filters.financial_year_id,
                        inputValue
                    );

            return [

                {
                    value:
                        null,

                    label:
                        "Full Financial Year",

                    data:
                        null,

                },

                ...(response || [])
                    .map(
                        period => ({

                            value:
                                period.id,

                            label:
                                period.period_name ||
                                period.period_code ||
                                `Period ${period.id}`,

                            data:
                                period,

                        })
                    ),

            ];

        } catch (error) {

            console.error(
                "Failed to load financial periods:",
                error
            );

            return [];

        }

    };


    // ==================================================================
    // LOAD BALANCE SHEET
    // ==================================================================

    const loadBalanceSheet =
        async () => {

            setLoading(
                true
            );

            setErrorMessage(
                ""
            );

            try {

                const response =
                    await balanceSheetService
                        .getBalanceSheet(
                            filters
                        );

                setReportData(
                    response ||
                    null
                );

                // ------------------------------------------------------
                // Reset drill-down when report changes.
                // ------------------------------------------------------

                setExpandedGroups(
                    {}
                );

                setExpandedAccounts(
                    {}

                );

                setAccountLedgers(
                    {}
                );

            } catch (error) {

                console.error(
                    "Failed to load Balance Sheet:",
                    error
                );

                setReportData(
                    null
                );

                setErrorMessage(
                    error?.response?.data?.message ||
                    error?.message ||
                    "Unable to load Balance Sheet."
                );

            } finally {

                setLoading(
                    false
                );

            }

        };


    // ==================================================================
    // SEARCH
    // ==================================================================

    const handleSearch =
        () => {

            if (
                !filters.company_id
            ) {

                setErrorMessage(
                    "Please select Company."
                );

                return;

            }


            if (
                !filters.financial_year_id
            ) {

                setErrorMessage(
                    "Please select Financial Year."
                );

                return;

            }


            loadBalanceSheet();

        };


    // ==================================================================
    // RESET
    // ==================================================================

    const handleReset =
        () => {

            setFilters({

                company_id:
                    null,

                financial_year_id:
                    null,

                financial_period_id:
                    null,

                include_zero_balances:
                    false,

            });


            setSelectedCompany(
                null
            );


            setSelectedFinancialYear(
                null
            );


            setSelectedFinancialPeriod(
                null
            );


            setReportData(
                null
            );


            setExpandedGroups(
                {}
            );


            setExpandedAccounts(
                {}
            );


            setAccountLedgers(
                {}
            );


            setErrorMessage(
                ""
            );

        };


    // ==================================================================
    // FORMAT AMOUNT
    // ==================================================================

    const formatAmount =
        value => {

            const amount =
                Number(
                    value || 0
                );

            return amount.toLocaleString(
                "en-IN",
                {
                    minimumFractionDigits:
                        2,

                    maximumFractionDigits:
                        2,
                }
            );

        };


    // ==================================================================
    // TOGGLE GROUP
    // ==================================================================

    const toggleGroup =
        groupId => {

            setExpandedGroups(
                previous => ({

                    ...previous,

                    [groupId]:
                        !previous[groupId],

                })
            );

        };


    // ==================================================================
    // TOGGLE ACCOUNT
    // ==================================================================

    const toggleAccount =
        async (
            account
        ) => {

            const accountId =
                account.account_id;


            const isExpanded =
                !!expandedAccounts[
                    accountId
                ];


            setExpandedAccounts(
                previous => ({

                    ...previous,

                    [accountId]:
                        !isExpanded,

                })
            );


            // ----------------------------------------------------------
            // Already loaded
            // ----------------------------------------------------------

            if (
                isExpanded ||
                accountLedgers[accountId]
            ) {

                return;

            }


            await loadAccountLedger(
                account
            );

        };


    // ==================================================================
    // LOAD ACCOUNT GENERAL LEDGER
    //
    // Uses the existing General Ledger API.
    //
    // Existing GL implementation already accepts:
    //
    // company_id
    // financial_year_id
    // financial_period_id
    // account_id
    //
    // and returns rows + summary.
    // ==================================================================

    // ======================================================================
// LOAD ACCOUNT GENERAL LEDGER
//
// IMPORTANT:
// Existing General Ledger API requires financial_period_id.
//
// Balance Sheet itself can operate at Financial Year level,
// therefore Financial Period remains optional for the Balance Sheet.
//
// If no Financial Period is selected, do NOT call General Ledger API.
// Show a clear message instead.
// ======================================================================

const loadAccountLedger =
    async (
        account
    ) => {

        const accountId =
            account.account_id;


        // --------------------------------------------------------------
        // GENERAL LEDGER REQUIRES FINANCIAL PERIOD
        // --------------------------------------------------------------

        if (
            !filters.financial_period_id
        ) {

            setAccountLedgers(
                previous => ({

                    ...previous,

                    [accountId]: {

                        loading:
                            false,

                        rows:
                            [],

                        summary:
                            null,

                        error:
                            "Financial Period is required to view General Ledger transactions. Please select a Financial Period above and run the Balance Sheet again.",

                    },

                })
            );

            return;

        }


        // --------------------------------------------------------------
        // START LOADING
        // --------------------------------------------------------------

        setAccountLedgers(
            previous => ({

                ...previous,

                [accountId]: {

                    loading:
                        true,

                    rows:
                        [],

                    summary:
                        null,

                    error:
                        "",

                },

            })
        );


        try {

            // ----------------------------------------------------------
            // EXISTING GENERAL LEDGER API
            // ----------------------------------------------------------

            const response =
                await generalLedgerService
                    .getAccountLedger({

                        company_id:
                            filters.company_id,

                        financial_year_id:
                            filters.financial_year_id,

                        financial_period_id:
                            filters.financial_period_id,

                        account_id:
                            accountId,

                    });


            // ----------------------------------------------------------
            // STORE LEDGER RESULT
            // ----------------------------------------------------------

            setAccountLedgers(
                previous => ({

                    ...previous,

                    [accountId]: {

                        loading:
                            false,

                        rows:
                            //response?.rows ||
                            response?.transactions ||

                            [],

                        summary:
                            response?.summary ||
                            null,

                        error:
                            "",

                    },

                })
            );


        } catch (error) {

            console.error(
                "Failed to load account General Ledger:",
                error
            );


            setAccountLedgers(
                previous => ({

                    ...previous,

                    [accountId]: {

                        loading:
                            false,

                        rows:
                            [],

                        summary:
                            null,

                        error:
                            error?.response
                                ?.data
                                ?.message ||
                            error?.message ||
                            "Unable to load account ledger.",

                    },

                })
            );

        }

    };


    // ==================================================================
    // OPEN JOURNAL
    //
    // Existing Journal View route:
    //
    // /finance/manual-journal/view/:id
    // ==================================================================

    const openJournal =
        journalHeaderId => {

            if (
                !journalHeaderId
            ) {

                return;

            }


            navigate(
                `/finance/manual-journal/view/${journalHeaderId}`
            );

        };


    // ==================================================================
    // EXPAND / COLLAPSE ALL
    // ==================================================================

    const expandAll =
        () => {

            if (
                !reportData
            ) {

                return;

            }


            const expanded = {};


            const collectGroups =
                group => {

                    if (!group) {

                        return;

                    }


                    expanded[
                        group.id
                    ] = true;


                    (
                        group.children ||
                        []
                    ).forEach(
                        child =>
                            collectGroups(
                                child
                            )
                    );

                };


            collectGroups(
                reportData.assets
            );

            collectGroups(
                reportData.liabilities
            );

            collectGroups(
                reportData.equity
            );


            setExpandedGroups(
                expanded
            );

        };


    const collapseAll =
        () => {

            setExpandedGroups(
                {}
            );

            setExpandedAccounts(
                {}
            );

        };


    // ==================================================================
    // SUMMARY CARDS
    // ==================================================================

    const summaryCards =
        useMemo(
            () => [

                {
                    title:
                        "Total Assets",

                    value:
                        formatAmount(
                            reportData
                                ?.total_assets
                        ),
                },

                {
                    title:
                        "Total Liabilities",

                    value:
                        formatAmount(
                            reportData
                                ?.total_liabilities
                        ),
                },

                {
                    title:
                        "Total Equity",

                    value:
                        formatAmount(
                            reportData
                                ?.total_equity
                        ),
                },

                {
                    title:
                        "Balance Check",

                    value:
                        reportData
                            ?.result ||
                        "-",
                },

            ],
            [
                reportData
            ]
        );


    // ==================================================================
    // RENDER LEDGER ROWS
    // ==================================================================

    const renderLedgerRows =
        account => {

            const accountId =
                account.account_id;


            const ledger =
                accountLedgers[
                    accountId
                ];


            if (!ledger) {

                return null;

            }


            return (

                <tr>

                    <td
                        colSpan="4"
                        className="p-0"
                    >

                        <div
                            className="bg-light border-top border-bottom"
                            style={{
                                marginLeft:
                                    "55px",
                            }}
                        >

                            {/* =================================================
                                LEDGER HEADER
                            ================================================= */}

                            <div
                                className="px-3 py-2 border-bottom"
                            >

                                <div
                                    className="d-flex justify-content-between align-items-center"
                                >

                                    <div>

                                        <strong>
                                            General Ledger
                                        </strong>

                                        <span
                                            className="text-muted ms-2"
                                        >
                                            {account.account_code}
                                            {" - "}
                                            {account.account_name}
                                        </span>

                                    </div>

                                    {
                                        ledger.summary &&
                                        (
                                            <div
                                                className="small text-muted"
                                            >

                                                Opening:
                                                {" "}
                                                {formatAmount(
                                                    ledger.summary
                                                        .opening_balance
                                                )}

                                                {" | "}

                                                Debit:
                                                {" "}
                                                {formatAmount(
                                                    ledger.summary
                                                        .total_debit
                                                )}

                                                {" | "}

                                                Credit:
                                                {" "}
                                                {formatAmount(
                                                    ledger.summary
                                                        .total_credit
                                                )}

                                                {" | "}

                                                Closing:
                                                {" "}
                                                {formatAmount(
                                                    ledger.summary
                                                        .closing_balance
                                                )}

                                            </div>
                                        )
                                    }

                                </div>

                            </div>


                            {/* =================================================
                                LOADING
                            ================================================= */}

                            {
                                ledger.loading && (

                                    <div
                                        className="text-center py-3"
                                    >

                                        <div
                                            className="spinner-border spinner-border-sm"
                                            role="status"
                                        />

                                        <span
                                            className="ms-2"
                                        >
                                            Loading transactions...
                                        </span>

                                    </div>

                                )
                            }


                            {/* =================================================
                                ERROR
                            ================================================= */}

                            {
                                !ledger.loading &&
                                ledger.error && (

                                    <div
                                        className="alert alert-danger m-3 mb-0"
                                    >

                                        {ledger.error}

                                    </div>

                                )
                            }


                            {/* =================================================
                                NO TRANSACTIONS
                            ================================================= */}

                            {
                                !ledger.loading &&
                                !ledger.error &&
                                ledger.rows.length === 0 && (

                                    <div
                                        className="text-center text-muted py-3"
                                    >

                                        No ledger transactions found.

                                    </div>

                                )
                            }


                            {/* =================================================
                                TRANSACTIONS
                            ================================================= */}

                            {
                                !ledger.loading &&
                                !ledger.error &&
                                ledger.rows.length > 0 && (

                                    <div
                                        className="table-responsive"
                                    >

                                        <table
                                            className="table table-sm table-hover mb-0"
                                        >

                                            <thead>

                                                <tr>

                                                    <th>
                                                        Date
                                                    </th>

                                                    <th>
                                                        Journal No.
                                                    </th>

                                                    <th>
                                                        Journal Type
                                                    </th>

                                                    <th>
                                                        Description
                                                    </th>

                                                    <th
                                                        className="text-end"
                                                    >
                                                        Debit
                                                    </th>

                                                    <th
                                                        className="text-end"
                                                    >
                                                        Credit
                                                    </th>

                                                    <th
                                                        className="text-end"
                                                    >
                                                        Running Balance
                                                    </th>

                                                    <th>
                                                    </th>

                                                </tr>

                                            </thead>


                                            <tbody>

                                                {
                                                    ledger.rows.map(
                                                        (
                                                            row,
                                                            index
                                                        ) => (

                                                            <tr
                                                                key={
                                                                    row.id ||
                                                                    row.journal_line_id ||
                                                                    index
                                                                }
                                                            >

                                                                <td>

                                                                    {
                                                                        row.date ||
                                                                        row.transaction_date ||
                                                                        "-"
                                                                    }

                                                                </td>


                                                                <td>

                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-link btn-sm p-0"
                                                                        onClick={() =>
                                                                            openJournal(
                                                                                row.journal_header_id
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            !row.journal_header_id
                                                                        }
                                                                    >

                                                                        {
                                                                            row.journal_no ||
                                                                            "-"
                                                                        }

                                                                    </button>

                                                                </td>


                                                                <td>

                                                                    {
                                                                        row.journal_type ||
                                                                        "-"
                                                                    }

                                                                </td>


                                                                <td>

                                                                    {
                                                                        row.description ||
                                                                        "-"
                                                                    }

                                                                </td>


                                                                <td
                                                                    className="text-end"
                                                                >

                                                                    {
                                                                        formatAmount(
                                                                            row.debit
                                                                        )
                                                                    }

                                                                </td>


                                                                <td
                                                                    className="text-end"
                                                                >

                                                                    {
                                                                        formatAmount(
                                                                            row.credit
                                                                        )
                                                                    }

                                                                </td>


                                                                <td
                                                                    className="text-end"
                                                                >

                                                                    {
                                                                        formatAmount(
                                                                            row.running_balance
                                                                        )
                                                                    }

                                                                </td>


                                                                <td
                                                                    className="text-end"
                                                                >

                                                                    {
                                                                        row.journal_header_id && (

                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-sm btn-outline-primary"
                                                                                title="View Journal"
                                                                                onClick={() =>
                                                                                    openJournal(
                                                                                        row.journal_header_id
                                                                                    )
                                                                                }
                                                                            >

                                                                                <i
                                                                                    className="fas fa-external-link-alt"
                                                                                />

                                                                            </button>

                                                                        )
                                                                    }

                                                                </td>

                                                            </tr>

                                                        )
                                                    )
                                                }

                                            </tbody>

                                        </table>

                                    </div>

                                )
                            }

                        </div>

                    </td>

                </tr>

            );

        };


    // ==================================================================
    // RENDER GROUP
    // ==================================================================

    const renderGroup =
        (
            group,
            level = 0
        ) => {

            if (!group) {

                return null;

            }


            const hasChildren =
                (
                    group.children ||
                    []
                ).length > 0;


            const hasAccounts =
                (
                    group.accounts ||
                    []
                ).length > 0;


            const hasExpandableContent =
                hasChildren ||
                hasAccounts;


            const isExpanded =
                !!expandedGroups[
                    group.id
                ];


            const indent =
                Math.min(
                    level * 24,
                    120
                );


            return (

                <React.Fragment
                    key={
                        `group-${group.id}`
                    }
                >

                    {/* ======================================================
                        GROUP ROW
                    ====================================================== */}

                    <tr>

                        <td
                            colSpan="3"
                            style={{
                                paddingLeft:
                                    `${indent + 12}px`,
                            }}
                        >

                            <div
                                className="d-flex align-items-center"
                            >

                                {
                                    hasExpandableContent ? (

                                        <button
                                            type="button"
                                            className="btn btn-sm btn-link text-decoration-none p-0 me-2"
                                            onClick={() =>
                                                toggleGroup(
                                                    group.id
                                                )
                                            }
                                            title={
                                                isExpanded
                                                    ? "Collapse"
                                                    : "Expand"
                                            }
                                        >

                                            <i
                                                className={
                                                    isExpanded
                                                        ? "fas fa-chevron-down"
                                                        : "fas fa-chevron-right"
                                                }
                                            />

                                        </button>

                                    ) : (

                                        <span
                                            className="me-2"
                                            style={{
                                                width:
                                                    "24px",
                                                display:
                                                    "inline-block",
                                            }}
                                        />

                                    )
                                }


                                <strong>
                                    {group.group_name}
                                </strong>

                            </div>

                        </td>


                        <td
                            className="text-end"
                        >

                            <strong>

                                {
                                    formatAmount(
                                        group.total
                                    )
                                }

                            </strong>

                        </td>

                    </tr>


                    {/* ======================================================
                        CHILD CONTENT
                    ====================================================== */}

                    {
                        isExpanded && (

                            <>

                                {/* ==========================================
                                    DIRECT COA ACCOUNTS
                                ========================================== */}

                                {
                                    (
                                        group.accounts ||
                                        []
                                    ).map(
                                        account => {

                                            const accountId =
                                                account.account_id;


                                            const accountExpanded =
                                                !!expandedAccounts[
                                                    accountId
                                                ];


                                            return (

                                                <React.Fragment
                                                    key={
                                                        `account-${accountId}`
                                                    }
                                                >

                                                    <tr>

                                                        <td
                                                            style={{
                                                                paddingLeft:
                                                                    `${indent + 36}px`,
                                                            }}
                                                        >

                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-link text-decoration-none p-0 me-2"
                                                                onClick={() =>
                                                                    toggleAccount(
                                                                        account
                                                                    )
                                                                }
                                                                title={
                                                                    accountExpanded
                                                                        ? "Collapse ledger"
                                                                        : "Drill down to General Ledger"
                                                                }
                                                            >

                                                                <i
                                                                    className={
                                                                        accountExpanded
                                                                            ? "fas fa-chevron-down"
                                                                            : "fas fa-chevron-right"
                                                                    }
                                                                />

                                                            </button>

                                                            <span
                                                                className="text-muted me-2"
                                                            >

                                                                {
                                                                    account.account_code
                                                                }

                                                            </span>

                                                        </td>


                                                        <td>

                                                            <span>

                                                                {
                                                                    account.account_name
                                                                }

                                                            </span>

                                                        </td>


                                                        <td>

                                                            {
                                                                account.account_nature ||
                                                                "-"
                                                            }

                                                        </td>


                                                        <td
                                                            className="text-end"
                                                        >

                                                            {
                                                                formatAmount(
                                                                    account.balance
                                                                )
                                                            }

                                                        </td>

                                                    </tr>


                                                    {/* ==================================
                                                        ACCOUNT LEDGER
                                                    ================================== */}

                                                    {
                                                        accountExpanded &&
                                                        renderLedgerRows(
                                                            account
                                                        )
                                                    }

                                                </React.Fragment>

                                            );

                                        }
                                    )
                                }


                                {/* ==========================================
                                    CHILD GROUPS
                                ========================================== */}

                                {
                                    (
                                        group.children ||
                                        []
                                    ).map(
                                        child =>
                                            renderGroup(
                                                child,
                                                level + 1
                                            )
                                    )
                                }

                            </>

                        )
                    }

                </React.Fragment>

            );

        };


    // ==================================================================
    // RENDER SECTION
    // ==================================================================

    const renderSection =
        (
            title,
            section
        ) => {

            if (!section) {

                return (

                    <div
                        className="card mt-3"
                    >

                        <div
                            className="card-header"
                        >

                            <strong>
                                {title}
                            </strong>

                        </div>

                        <div
                            className="card-body text-center py-4"
                        >

                            No {title} data found.

                        </div>

                    </div>

                );

            }


            return (

                <div
                    className="card mt-3"
                >

                    {/* ==================================================
                        SECTION HEADER
                    ================================================== */}

                    <div
                        className="card-header d-flex justify-content-between align-items-center"
                    >

                        <strong>
                            {title}
                        </strong>


                        <strong>

                            ₹{" "}
                            {
                                formatAmount(
                                    section.total
                                )
                            }

                        </strong>

                    </div>


                    {/* ==================================================
                        TABLE
                    ================================================== */}

                    <div
                        className="card-body p-0"
                    >

                        <div
                            className="table-responsive"
                        >

                            <table
                                className="table table-hover table-bordered mb-0"
                            >

                                <thead>

                                    <tr>

                                        <th
                                            style={{
                                                width:
                                                    "20%",
                                            }}
                                        >
                                            Account Code
                                        </th>

                                        <th>
                                            Account / Group
                                        </th>

                                        <th>
                                            Nature
                                        </th>

                                        <th
                                            className="text-end"
                                            style={{
                                                width:
                                                    "18%",
                                            }}
                                        >
                                            Amount
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {
                                        loading ? (

                                            <tr>

                                                <td
                                                    colSpan="4"
                                                    className="text-center py-4"
                                                >

                                                    Loading Balance Sheet...

                                                </td>

                                            </tr>

                                        ) : (

                                            renderGroup(
                                                section
                                            )

                                        )
                                    }

                                </tbody>


                                <tfoot>

                                    <tr>

                                        <th
                                            colSpan="3"
                                            className="text-end"
                                        >

                                            Total {title}

                                        </th>

                                        <th
                                            className="text-end"
                                        >

                                            {
                                                formatAmount(
                                                    section.total
                                                )
                                            }

                                        </th>

                                    </tr>

                                </tfoot>

                            </table>

                        </div>

                    </div>

                </div>

            );

        };


    // ==================================================================
    // RENDER
    // ==================================================================

    return (

        <div
            className="container-fluid"
        >

            {/* ==========================================================
                HEADER
            ========================================================== */}

            <FinancePageHeader
                title="Balance Sheet"
                subtitle="Statement of Financial Position"
            />


            {/* ==========================================================
                SUMMARY CARDS
            ========================================================== */}

            <FinanceSummaryCards
                cards={
                    summaryCards
                }
            />


            {/* ==========================================================
                FILTER PANEL
            ========================================================== */}

            <FinanceSearchPanel
                loading={
                    loading
                }
                onSearch={
                    handleSearch
                }
                onReset={
                    handleReset
                }
            >

                {/* ======================================================
                    COMPANY
                ====================================================== */}

                <div
                    className="col-lg-4 col-md-6"
                >

                    <label
                        className="form-label"
                    >

                        Company

                        <span
                            className="text-danger"
                        >
                            {" "}*
                        </span>

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadCompanies
                        }
                        isClearable
                        value={
                            selectedCompany
                        }
                        onChange={
                            option => {

                                setSelectedCompany(
                                    option
                                );


                                setSelectedFinancialPeriod(
                                    null
                                );


                                updateFilter(
                                    "company_id",
                                    option?.value ||
                                    null
                                );


                                updateFilter(
                                    "financial_period_id",
                                    null
                                );

                            }
                        }
                    />

                </div>


                {/* ======================================================
                    FINANCIAL YEAR
                ====================================================== */}

                <div
                    className="col-lg-4 col-md-6"
                >

                    <label
                        className="form-label"
                    >

                        Financial Year

                        <span
                            className="text-danger"
                        >
                            {" "}*
                        </span>

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadFinancialYears
                        }
                        isClearable
                        value={
                            selectedFinancialYear
                        }
                        onChange={
                            option => {

                                setSelectedFinancialYear(
                                    option
                                );


                                setSelectedFinancialPeriod(
                                    null
                                );


                                updateFilter(
                                    "financial_year_id",
                                    option?.value ||
                                    null
                                );


                                updateFilter(
                                    "financial_period_id",
                                    null
                                );

                            }
                        }
                    />

                </div>


                {/* ======================================================
                    FINANCIAL PERIOD
                ====================================================== */}

                <div
                    className="col-lg-4 col-md-6"
                >

                    <label
                        className="form-label"
                    >

                        Financial Period

                        <span
                            className="text-muted ms-1"
                        >
                            (Optional)
                        </span>

                    </label>


                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={
                            loadFinancialPeriods
                        }
                        isClearable
                        isDisabled={
                            !filters.financial_year_id
                        }
                        value={
                            selectedFinancialPeriod
                        }
                        onChange={
                            option => {

                                setSelectedFinancialPeriod(
                                    option
                                );

                                updateFilter(
                                    "financial_period_id",
                                    option?.value ||
                                    null
                                );

                                // --------------------------------------------------------------
                                // Clear previously loaded account ledgers because the
                                // financial period has changed.
                                // --------------------------------------------------------------

                                setExpandedAccounts(
                                    {}
                                );

                                setAccountLedgers(
                                    {}
                                );

                            }
                        }
                    />

                </div>


                {/* ======================================================
                    ZERO BALANCES
                ====================================================== */}

                <div
                    className="col-12 mt-2"
                >

                    <div
                        className="form-check"
                    >

                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="includeZeroBalances"
                            checked={
                                filters
                                    .include_zero_balances
                            }
                            onChange={
                                event =>
                                    updateFilter(
                                        "include_zero_balances",
                                        event.target.checked
                                    )
                            }
                        />


                        <label
                            className="form-check-label"
                            htmlFor="includeZeroBalances"
                        >

                            Include Zero Balance Accounts

                        </label>

                    </div>

                </div>

            </FinanceSearchPanel>


            {/* ==========================================================
                ERROR
            ========================================================== */}

            {
                errorMessage && (

                    <div
                        className="alert alert-danger mt-3"
                    >

                        {errorMessage}

                    </div>

                )
            }


            {/* ==========================================================
                TOOLBAR
            ========================================================== */}

            {
                reportData && (

                    <div
                        className="card mt-3"
                    >

                        <div
                            className="card-body py-2"
                        >

                            <div
                                className="d-flex justify-content-between align-items-center flex-wrap gap-2"
                            >

                                <div>

                                    <strong>
                                        Drill Down
                                    </strong>

                                    <span
                                        className="text-muted ms-2"
                                    >
                                        Expand groups and accounts
                                        to view ledger transactions.
                                    </span>

                                </div>


                                <div
                                    className="d-flex gap-2"
                                >

                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={
                                            expandAll
                                        }
                                    >

                                        <i
                                            className="fas fa-expand-arrows-alt me-1"
                                        />

                                        Expand All

                                    </button>


                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={
                                            collapseAll
                                        }
                                    >

                                        <i
                                            className="fas fa-compress-arrows-alt me-1"
                                        />

                                        Collapse All

                                    </button>

                                </div>

                            </div>

                        </div>

                    </div>

                )
            }


            {/* ==========================================================
                BALANCE STATUS
            ========================================================== */}

            {
                reportData && (

                    <div
                        className="card mt-3"
                    >

                        <div
                            className="card-body"
                        >

                            <div
                                className={
                                    reportData.is_balanced
                                        ? "alert alert-success mb-0"
                                        : "alert alert-danger mb-0"
                                }
                            >

                                <strong>
                                    Balance Sheet:
                                </strong>

                                {" "}

                                {
                                    reportData.is_balanced
                                        ? "BALANCED"
                                        : "NOT BALANCED"
                                }

                            </div>

                        </div>

                    </div>

                )
            }


            {/* ==========================================================
                ASSETS
            ========================================================== */}

            {
                reportData &&
                renderSection(
                    "Assets",
                    reportData.assets
                )
            }


            {/* ==========================================================
                LIABILITIES
            ========================================================== */}

            {
                reportData &&
                renderSection(
                    "Liabilities",
                    reportData.liabilities
                )
            }


            {/* ==========================================================
                EQUITY
            ========================================================== */}

            {
                reportData &&
                renderSection(
                    "Equity",
                    reportData.equity
                )
            }


            {/* ==========================================================
                CURRENT YEAR PROFIT / LOSS
            ========================================================== */}

            {
                reportData && (

                    <div
                        className="card mt-3"
                    >

                        <div
                            className="card-header"
                        >

                            <strong>
                                Current Year Profit / Loss
                            </strong>

                        </div>


                        <div
                            className="card-body"
                        >

                            <div
                                className="row"
                            >

                                <div
                                    className="col-md-8 text-end"
                                >

                                    Current Year Profit / Loss

                                </div>


                                <div
                                    className="col-md-4 text-end"
                                >

                                    <strong>

                                        {
                                            formatAmount(
                                                reportData
                                                    .current_year_profit_loss
                                            )
                                        }

                                    </strong>

                                </div>

                            </div>

                        </div>

                    </div>

                )
            }


            {/* ==========================================================
                BALANCE SHEET CHECK
            ========================================================== */}

            {
                reportData && (

                    <div
                        className="card mt-3 mb-4"
                    >

                        <div
                            className="card-header"
                        >

                            <strong>
                                Balance Sheet Check
                            </strong>

                        </div>


                        <div
                            className="card-body"
                        >

                            <div
                                className="row mb-2"
                            >

                                <div
                                    className="col-md-8 text-end"
                                >

                                    <strong>
                                        Total Assets
                                    </strong>

                                </div>


                                <div
                                    className="col-md-4 text-end"
                                >

                                    <strong>

                                        {
                                            formatAmount(
                                                reportData
                                                    .total_assets
                                            )
                                        }

                                    </strong>

                                </div>

                            </div>


                            <div
                                className="row mb-2"
                            >

                                <div
                                    className="col-md-8 text-end"
                                >

                                    <strong>
                                        Total Liabilities + Equity
                                    </strong>

                                </div>


                                <div
                                    className="col-md-4 text-end"
                                >

                                    <strong>

                                        {
                                            formatAmount(
                                                reportData
                                                    .liabilities_plus_equity
                                            )
                                        }

                                    </strong>

                                </div>

                            </div>


                            <div
                                className="row mb-2"
                            >

                                <div
                                    className="col-md-8 text-end"
                                >

                                    Difference

                                </div>


                                <div
                                    className="col-md-4 text-end"
                                >

                                    {
                                        formatAmount(
                                            reportData
                                                .difference
                                        )
                                    }

                                </div>

                            </div>


                            <hr />


                            <div
                                className="row"
                            >

                                <div
                                    className="col-md-8 text-end"
                                >

                                    <strong>
                                        Status
                                    </strong>

                                </div>


                                <div
                                    className="col-md-4 text-end"
                                >

                                    <strong>

                                        {
                                            reportData
                                                .result ||
                                            "-"
                                        }

                                    </strong>

                                </div>

                            </div>

                        </div>

                    </div>

                )
            }

        </div>

    );

}